import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, CameraOff, ScanLine, Loader2 } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";

// Reusable QR sticker scanner. Stays open for batch scanning; fires onToken(token)
// for each decoded code (deduped) and also supports manual entry + Enter.
export default function QrScannerModal({ open, onClose, onToken, title = "Scan QR stickers", hint = "" }) {
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState("");
  const scannerRef = useRef(null);
  const lastRef = useRef({ text: "", ts: 0 });
  const inputRef = useRef(null);

  const handle = useCallback((text) => {
    const t = (text || "").trim();
    if (!t) return;
    const now = Date.now();
    if (t === lastRef.current.text && now - lastRef.current.ts < 1500) return;
    lastRef.current = { text: t, ts: now };
    onToken(t);
  }, [onToken]);

  useEffect(() => { if (open) { setScanning(true); setError(""); } else setScanning(false); }, [open]);

  useEffect(() => {
    if (!open || !scanning) return;
    const scanner = new Html5Qrcode("qr-scanner-region", { verbose: false });
    scannerRef.current = scanner;
    scanner.start({ facingMode: "environment" }, { fps: 12, qrbox: { width: 220, height: 220 } },
      (text) => handle(text), () => {}).catch(() => setError("Could not start the camera. Use the box below to type/scan a code, or open in a new tab."));
    return () => { const s = scannerRef.current; if (s) s.stop().then(() => s.clear()).catch(() => {}); };
  }, [open, scanning, handle]);

  if (!open) return null;
  const onKey = (e) => { if (e.key === "Enter") { e.preventDefault(); handle(inputRef.current.value); inputRef.current.value = ""; } };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(5,7,12,0.8)", backdropFilter: "blur(4px)" }} data-testid="qr-scanner-modal">
      <div className="w-full max-w-md rounded-2xl border overflow-hidden" style={{ background: "#0D1017", borderColor: "var(--pk-border)" }}>
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: "var(--pk-border)" }}>
          <div>
            <h3 className="font-heading text-lg font-bold text-slate-100 flex items-center gap-2"><ScanLine className="h-5 w-5 text-amber-400" /> {title}</h3>
            {hint ? <p className="text-xs text-slate-500 mt-0.5">{hint}</p> : null}
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-200" data-testid="qr-scanner-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div id="qr-scanner-region" className="w-full rounded-xl overflow-hidden border" style={{ borderColor: "var(--pk-border)", minHeight: 240 }} />
          {error && <div className="flex items-center gap-2 text-rose-400 text-xs" data-testid="qr-scanner-error"><CameraOff className="h-4 w-4" /> {error}</div>}
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg border focus-within:ring-2 focus-within:ring-amber-500/50" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
            <ScanLine className="h-4 w-4 text-slate-500" />
            <input ref={inputRef} onKeyDown={onKey} data-testid="qr-scanner-input"
              placeholder="or scan/type code + Enter" className="w-full bg-transparent outline-none text-xs text-slate-100 placeholder:text-slate-600 font-mono-num" />
          </div>
          <button onClick={onClose} data-testid="qr-scanner-done"
            className="w-full py-2.5 rounded-xl bg-amber-500 text-[#1a1205] font-semibold text-sm hover:bg-amber-400">Done</button>
        </div>
      </div>
    </div>,
    document.body
  );
}
